/**
 * EquipmentSurvey Component
 *
 * Interactive survey for clients to select their available equipment.
 * Equipment is organized by category for easy selection.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import {
  Dumbbell,
  Bike,
  Target,
  Layers,
  CircleDot,
  Armchair,
  Grid3X3,
  Wrench,
  CheckCircle2,
} from 'lucide-react';
import { EQUIPMENT_CATEGORIES } from '@/hooks/use-client-fitness-profile';

// Equipment display names
const EQUIPMENT_LABELS: Record<string, string> = {
  barbell: 'Standard Barbell',
  olympic_barbell: 'Olympic Barbell',
  ez_curl_bar: 'EZ Curl Bar',
  trap_bar: 'Trap/Hex Bar',
  dumbbells: 'Dumbbells',
  adjustable_dumbbells: 'Adjustable Dumbbells',
  cable_machine: 'Cable Machine',
  smith_machine: 'Smith Machine',
  leg_press: 'Leg Press',
  chest_press_machine: 'Chest Press Machine',
  lat_pulldown: 'Lat Pulldown',
  seated_row_machine: 'Seated Row Machine',
  leg_curl: 'Leg Curl Machine',
  leg_extension: 'Leg Extension',
  hack_squat: 'Hack Squat',
  treadmill: 'Treadmill',
  elliptical: 'Elliptical',
  stationary_bike: 'Stationary Bike',
  rowing_machine: 'Rowing Machine',
  stair_climber: 'Stair Climber',
  pull_up_bar: 'Pull-up Bar',
  dip_station: 'Dip Station',
  gymnastics_rings: 'Gymnastics Rings',
  resistance_bands: 'Resistance Bands',
  cable_attachments: 'Cable Attachments',
  kettlebells: 'Kettlebells',
  flat_bench: 'Flat Bench',
  incline_bench: 'Incline Bench',
  decline_bench: 'Decline Bench',
  adjustable_bench: 'Adjustable Bench',
  squat_rack: 'Squat Rack',
  power_rack: 'Power Rack',
  half_rack: 'Half Rack',
  ab_roller: 'Ab Roller',
  foam_roller: 'Foam Roller',
  medicine_ball: 'Medicine Ball',
  stability_ball: 'Stability Ball',
  battle_ropes: 'Battle Ropes',
};

// Category configuration
const CATEGORY_CONFIG: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }>; description: string }
> = {
  barbells: {
    label: 'Barbells',
    icon: Dumbbell,
    description: 'Bar-based strength training equipment',
  },
  dumbbells: {
    label: 'Dumbbells',
    icon: Dumbbell,
    description: 'Free weights for versatile training',
  },
  machines: {
    label: 'Machines',
    icon: Target,
    description: 'Weight machines and cable systems',
  },
  cardio: {
    label: 'Cardio Equipment',
    icon: Bike,
    description: 'Cardiovascular training machines',
  },
  bodyweight: {
    label: 'Bodyweight',
    icon: CircleDot,
    description: 'Equipment for calisthenics and bodyweight exercises',
  },
  resistance: {
    label: 'Resistance',
    icon: Layers,
    description: 'Bands and cable accessories',
  },
  kettlebells: {
    label: 'Kettlebells',
    icon: Dumbbell,
    description: 'Cast iron weights for dynamic movements',
  },
  benches: {
    label: 'Benches',
    icon: Armchair,
    description: 'Various bench configurations',
  },
  racks: {
    label: 'Racks',
    icon: Grid3X3,
    description: 'Squat racks and power cages',
  },
  accessories: {
    label: 'Accessories',
    icon: Wrench,
    description: 'Additional training tools',
  },
};

// Quick select presets
const PRESETS = {
  commercial_gym: {
    label: 'Full Commercial Gym',
    description: 'Access to all standard gym equipment',
    equipment: [
      'barbell',
      'olympic_barbell',
      'ez_curl_bar',
      'dumbbells',
      'cable_machine',
      'smith_machine',
      'leg_press',
      'chest_press_machine',
      'lat_pulldown',
      'seated_row_machine',
      'leg_curl',
      'leg_extension',
      'treadmill',
      'elliptical',
      'stationary_bike',
      'pull_up_bar',
      'dip_station',
      'kettlebells',
      'flat_bench',
      'incline_bench',
      'adjustable_bench',
      'squat_rack',
      'power_rack',
      'foam_roller',
      'stability_ball',
    ],
  },
  home_gym: {
    label: 'Home Gym Setup',
    description: 'Typical home gym with basics',
    equipment: [
      'barbell',
      'dumbbells',
      'adjustable_dumbbells',
      'pull_up_bar',
      'resistance_bands',
      'kettlebells',
      'adjustable_bench',
      'squat_rack',
      'foam_roller',
    ],
  },
  minimal: {
    label: 'Minimal Equipment',
    description: 'Just the essentials',
    equipment: ['dumbbells', 'resistance_bands', 'pull_up_bar', 'foam_roller'],
  },
  bodyweight_only: {
    label: 'Bodyweight Only',
    description: 'No equipment required',
    equipment: ['pull_up_bar', 'dip_station'],
  },
};

interface EquipmentSurveyProps {
  value: string[];
  onChange: (equipment: string[]) => void;
  disabled?: boolean;
  showPresets?: boolean;
}

export default function EquipmentSurvey({
  value,
  onChange,
  disabled = false,
  showPresets = true,
}: EquipmentSurveyProps) {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const handleEquipmentToggle = (equipmentId: string) => {
    if (disabled) return;

    if (value.includes(equipmentId)) {
      onChange(value.filter((e) => e !== equipmentId));
    } else {
      onChange([...value, equipmentId]);
    }
  };

  const handleCategorySelectAll = (category: string) => {
    if (disabled) return;

    const categoryItems =
      EQUIPMENT_CATEGORIES[category as keyof typeof EQUIPMENT_CATEGORIES] || [];
    const allSelected = categoryItems.every((item) => value.includes(item));

    if (allSelected) {
      // Deselect all in category
      onChange(value.filter((e) => !categoryItems.includes(e)));
    } else {
      // Select all in category
      const newSelection = new Set([...value, ...categoryItems]);
      onChange(Array.from(newSelection));
    }
  };

  const handlePresetSelect = (presetKey: string) => {
    if (disabled) return;

    const preset = PRESETS[presetKey as keyof typeof PRESETS];
    if (preset) {
      onChange(preset.equipment);
    }
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const getCategorySelectionCount = (category: string) => {
    const categoryItems =
      EQUIPMENT_CATEGORIES[category as keyof typeof EQUIPMENT_CATEGORIES] || [];
    return categoryItems.filter((item) => value.includes(item)).length;
  };

  return (
    <div className="space-y-6">
      {/* Quick Presets */}
      {showPresets && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick Select</CardTitle>
            <CardDescription>Choose a preset or select equipment manually below</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(PRESETS).map(([key, preset]) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePresetSelect(key)}
                  disabled={disabled}
                  className="h-auto min-h-[3.25rem] w-full min-w-0 py-2 px-3 flex flex-col items-start justify-start gap-0.5 text-left whitespace-normal"
                >
                  <span className="w-full font-medium text-xs truncate">{preset.label}</span>
                  <span className="w-full text-[11px] leading-tight text-muted-foreground line-clamp-2">
                    {preset.description}
                  </span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selection Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <span className="font-medium">{value.length} items selected</span>
        </div>
        {value.length > 0 && (
          <Button variant="ghost" size="sm" onClick={handleClearAll} disabled={disabled}>
            Clear All
          </Button>
        )}
      </div>

      {/* Equipment Categories */}
      <Accordion
        type="multiple"
        value={expandedCategories}
        onValueChange={setExpandedCategories}
        className="space-y-2"
      >
        {Object.entries(EQUIPMENT_CATEGORIES).map(([categoryKey, items]) => {
          const config = CATEGORY_CONFIG[categoryKey];
          const selectionCount = getCategorySelectionCount(categoryKey);
          const allSelected = items.every((item) => value.includes(item));
          const Icon = config?.icon || Dumbbell;

          return (
            <AccordionItem
              key={categoryKey}
              value={categoryKey}
              className="border rounded-lg px-4 data-[state=open]:bg-muted/30"
            >
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center justify-between w-full pr-2">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <div className="text-left">
                      <span className="font-medium">{config?.label || categoryKey}</span>
                      <p className="text-xs text-muted-foreground">
                        {config?.description || ''}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={selectionCount > 0 ? 'default' : 'secondary'}
                    className="ml-2"
                  >
                    {selectionCount}/{items.length}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-3">
                  {/* Select All for Category */}
                  <div className="flex items-center justify-between py-2 border-b">
                    <Label className="text-sm font-medium">Select All</Label>
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() => handleCategorySelectAll(categoryKey)}
                      disabled={disabled}
                    />
                  </div>

                  {/* Individual Equipment Items */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {items.map((equipmentId) => {
                      const isSelected = value.includes(equipmentId);
                      const label =
                        EQUIPMENT_LABELS[equipmentId] ||
                        equipmentId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

                      return (
                        <div
                          key={equipmentId}
                          className={cn(
                            'flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors',
                            isSelected
                              ? 'bg-primary/10 border-primary/50'
                              : 'bg-background hover:bg-muted/50',
                            disabled && 'opacity-50 cursor-not-allowed'
                          )}
                          onClick={() => handleEquipmentToggle(equipmentId)}
                        >
                          <Label
                            className={cn(
                              'text-sm cursor-pointer',
                              isSelected && 'font-medium'
                            )}
                          >
                            {label}
                          </Label>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleEquipmentToggle(equipmentId)}
                            disabled={disabled}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
